/**
 * Client-side token utilities.
 *
 * NOTE: This module re-exports shared masking utilities for client-side use.
 * Token validation and generation happen server-side.
 */

import { z } from "zod";

// Re-export masking utilities from shared module
export {
  maskRfc,
  maskRazonSocial,
  parsePartialRfc,
  parsePartialRazonSocial,
  validateRfcMatch,
  validateRazonSocialMatch,
} from "~/shared/masking";

// Token payload schema (client-side version)
const tokenPayloadSchema = z.object({
  // Internal enterprise ID
  enterpriseId: z.string(),
  // Partial RFC (first 3 and last 3 characters visible, middle masked)
  partialRfc: z.string(),
  // Partial Razón Social (first and last 3 characters visible)
  partialRazonSocial: z.string(),
  // Token issued at (Unix timestamp in seconds)
  iat: z.number(),
  // Token expiration (Unix timestamp in seconds)
  exp: z.number(),
  // Unique token ID for one-time use validation
  jti: z.string(),
});

export type TokenPayload = z.infer<typeof tokenPayloadSchema>;

export interface TokenValidationResult {
  valid: boolean;
  payload?: TokenPayload;
  error?: "expired" | "invalid" | "used" | "missing";
  remainingTime?: number;
}

// Token expiration time in milliseconds (10 minutes)
export const TOKEN_EXPIRATION_MS = 10 * 60 * 1000;
